import {
	RESOURCES_NAV_ITEMS,
	BATCH_CLAIMS_NAV_ITEMS,
	HELP_NAV_ITEMS,
	TOP_NAV_ITEMS,
	FOOTER_ITEMS,
	TOP_SEARCH_NAV_ITEMS,
	MESSAGE_CENTER,
	MY_ACCOUNT_PROFILE_NAV_ITEMS,
	LOGOUT_URL,
} from './navigationItems';
import {
	pageKey,
	permissionName,
	permissionOperation,
	retrievePermissionWithKey,
} from './pageConstants';

export const getRedirectionLink = (page, redirectionLinks) =>
	redirectionLinks.filter(link => link.pageKey === page)[0]?.redirectUrl ||
	null;

// Use the nav's legacy redirection path if appropriate
export const updateNavsRedirectionPath = (navs, redirectionLinks) => {
	navs.forEach(nav => {
		const legacyLink = getRedirectionLink(nav.key, redirectionLinks);
		if (legacyLink) {
			nav.path = legacyLink;
			nav.isLegacy = true;
		}
	});
};

// Get all nav items that the user has permission to see and access
const getAccessibleDropdownNavs = (
	unfilteredNavItems,
	userSessionPermissions
) =>
	unfilteredNavItems.filter(navItem => {
		const pagePermission = retrievePermissionWithKey[navItem.key];
		// retrievePermissionWithKey is a dictionary of strings and string arrays so we need to handle both
		if (Array.isArray(pagePermission)) {
			// The user only needs one of the permissions in the array to see and access the page
			for (let i = 0; i < pagePermission.length; i += 1) {
				if (userSessionPermissions.includes(pagePermission[i])) {
					return true;
				}
			}
			return false;
		}
		return userSessionPermissions.includes(pagePermission);
	});

const sortNavigationItems = navs => navs.sort((a, b) => a.order - b.order);

export const getResourcesNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(RESOURCES_NAV_ITEMS, permissions);

	updateNavsRedirectionPath(navs, redirectionLinks);
	sortNavigationItems(navs);

	return navs;
};

export const getBatchClaimsNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(BATCH_CLAIMS_NAV_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);
	sortNavigationItems(navs);

	return navs;
};

export const getHelpNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(HELP_NAV_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);
	sortNavigationItems(navs);

	return navs;
};

export const getMessageCenterPath = redirectionLinks => {
	updateNavsRedirectionPath([MESSAGE_CENTER], redirectionLinks);
	return MESSAGE_CENTER.path;
};

export const getFooterNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(FOOTER_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);

	return navs;
};

export const getSearchNavs = (permissions, redirectionLinks) => {
	// If the user has the health plan member search permission, only show the legacy member search page
	if (
		permissions.includes(
			`${permissionName.healthPlanMemberSearch}:${permissionOperation.read}`
		)
	) {
		const healthPlanMemberSearchRedirectUrl = redirectionLinks.find(
			redirectionLink =>
				redirectionLink.pageKey === pageKey.healthPlanMemberSearch
		)?.redirectUrl;
		return [
			{
				key: pageKey.healthPlanMemberSearch,
				label: 'Member Search',
				order: 1,
				isLegacy: !!healthPlanMemberSearchRedirectUrl,
				path: healthPlanMemberSearchRedirectUrl ?? null,
			},
		];
	}

	const navs = getAccessibleDropdownNavs(TOP_SEARCH_NAV_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);
	sortNavigationItems(navs);

	return navs;
};

// Some items in the navigation bar in the header have a menu function rather in addition to being a link button
// this function makes clicking the item function the same as clikcing the first item in the menu
const headerNavsHelper = (navs, items, navKey, redirectionLinks) => {
	if (items && items.length > 0) {
		const topNav = TOP_NAV_ITEMS.filter(
			navItem => navItem.key === navKey
		)[0];
		topNav.menuItems = items;
		const redirectLink = getRedirectionLink(items[0].key, redirectionLinks);
		topNav.path = redirectLink || items[0].path;
		navs.push(topNav);
	}
};

export const getHeaderNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(TOP_NAV_ITEMS, permissions);

	headerNavsHelper(
		navs,
		getResourcesNavs(permissions, redirectionLinks),
		'resources',
		redirectionLinks
	);

	headerNavsHelper(
		navs,
		getBatchClaimsNavs(permissions, redirectionLinks),
		'batch-claims',
		redirectionLinks
	);

	headerNavsHelper(
		navs,
		getHelpNavs(permissions, redirectionLinks),
		'help',
		redirectionLinks
	);

	headerNavsHelper(
		navs,
		getSearchNavs(permissions, redirectionLinks),
		'search',
		redirectionLinks
	);

	updateNavsRedirectionPath(navs, redirectionLinks);

	sortNavigationItems(navs);
	return navs;
};

export const getHomePagePath = (permissions, redirectionLinks) => {
	const homePath = getHeaderNavs(permissions, redirectionLinks).filter(
		item => item.key === pageKey.home
	)[0]?.path;
	return homePath;
};

// We combine the logic for the my account left nav for my account and the profile menu in the header here because they should always have the same menu items
export const getMyAccountProfileNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(
		MY_ACCOUNT_PROFILE_NAV_ITEMS,
		permissions
	);

	updateNavsRedirectionPath(navs, redirectionLinks);

	// The log out item is handled as a special case since it isn't associated with a permission and doeesn't have a feature flag
	navs.push({
		key: pageKey.logOut,
		path: LOGOUT_URL,
		label: 'Log Out',
		order: 7,
		isLegacy: true,
	});

	sortNavigationItems(navs);

	return navs;
};

export const getMyAccountNavs = (permissions, redirectionLinks) =>
	getMyAccountProfileNavs(permissions, redirectionLinks);

export const getProfileNavs = (permissions, redirectionLinks) =>
	getMyAccountProfileNavs(permissions, redirectionLinks);
